import { useEffect, useState } from "react";
import IsRequired from "../../../../formcontrols/common/IsRequired";
import { useQuery } from "@tanstack/react-query";
import { get } from "../../../../../api/util";
import {
  CvrOpslagDto,
  IProduktionsenhed,
} from "../../../../../interfaces/firma";
import { FormikProps } from "formik";
import ValidationErrorMessage from "../../../../formcontrols/ValidationErrorMessage";
import { AxiosError } from "axios";
import Spinner from "../../../../spinner/Spinner";

type RequiredTypes = {
  produktionsenhed: IProduktionsenhed | undefined;
  inCVR: string;
};
export function useCvrOpslag<T extends RequiredTypes>(formik: FormikProps<T>) {
  const initial = formik.values["produktionsenhed"];
  const [cvrNr, setCvrNr] = useState<string>(
    initial ? initial.cvrnummer.toString() : "",
  );
  // 37458929
  const [pNr, setPNr] = useState<string>(
    initial ? initial.pnummer.toString() : "",
  );
  // 102116475
  const { data, status, error } = useQuery<
    CvrOpslagDto,
    AxiosError<CVROpslagRequestError>,
    CvrOpslagDto,
    string[]
  >({
    queryKey: [pNr, cvrNr, "cvropslag"],
    gcTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    enabled: cvrNr.length === 8 && pNr.length === 10,
    queryFn: () => {
      return get<CvrOpslagDto>({
        query: `grunddata/cvr?cvrNr=${cvrNr}&pNr=${pNr}`,
      });
    },
  });
  const produktionsEnhed: IProduktionsenhed | undefined = data
    ? {
        navn: data.navn ?? "",
        cvrnummer: data.cvrNr,
        pnummer: data.pNr,
        adgangsadressebetegnelse: data.beliggenhedsadresse.toLines.join(", "),
        adresse: data.beliggenhedsadresse.toLines.join(", "),
        kommunekode: "",
        branche: "",
        adresseid: data.beliggenhedsadresse.darAdresseId ?? "",
        branchekode: "",
        er_aktiv: true,
        geometri: { type: "Point", coordinates: [0, 0], crs: {} },
        gruppekode: "",
        hovedafdelingkode: "",
        hovedgruppekode: "",
        husnummerid: "",
        reklamebeskyttet: "False",
        sammensatstatus: "Aktiv",
        septima_antal_ansatte_seneste: 0,
        septima_email: "",
        septima_search_document: "",
        septima_search_document_adgangsadressebetegnelse: "",
        septima_search_document_fornavn: "",
        septima_search_document_navn: "",
        septima_tlf: "",
        undergruppekode: "",
      }
    : undefined;

  useEffect(() => {
    if (
      produktionsEnhed?.cvrnummer !==
        formik.values.produktionsenhed?.cvrnummer &&
      formik.values["inCVR"] === "yes-manual-input"
    ) {
      formik.setFieldValue("produktionsenhed", produktionsEnhed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [produktionsEnhed]);
  return {
    cvrNr,
    setCvrNr,
    pNr,
    setPNr,
    produktionsEnhed,
    status,
    error,
  };
}

type CVROpslagProps = {
  cvrNr: string;
  setCvrNr: (cvrNr: string) => void;
  pNr: string;
  setPNr: (pNr: string) => void;
  status: "error" | "success" | "pending";
  produktionsEnhed: IProduktionsenhed | undefined;
  error: AxiosError<CVROpslagRequestError> | null;
};

type CVROpslagRequestError = {
  Message: string;
  StatusCode: number;
};

export default function CvrOpslag(props: CVROpslagProps) {
  const { cvrNr, pNr, setCvrNr, setPNr, status, produktionsEnhed, error } =
    props;
  const [cvrNrTouched, setCvrNrTouched] = useState<boolean>(false);
  const [pNrTouched, setPNrTouched] = useState<boolean>(false);
  const cvrNrError = () => {
    if (cvrNr.length !== 8) {
      return "CVR-nr skal have 8 cifre";
    }
    if (cvrNr.length === 8 && isNaN(Number(cvrNr))) {
      return "CVR-nr skal være tal";
    }
    return "";
  };
  const pNrError = () => {
    if (pNr.length !== 10) {
      return "P-nr skal have 10 cifre";
    }
    if (pNr.length === 10 && isNaN(Number(pNr))) {
      return "P-nr skal være tal";
    }
    return "";
  };
  return (
    <>
      <div className="w-100 flex-row gap-3">
        <div className="w-50 position-relative">
          <label className="form-label" htmlFor="cvr-nr">
            CVR-nummer <IsRequired />
          </label>
          <input
            type="text"
            className={`form-control${
              cvrNrTouched && cvrNrError() ? " is-invalid" : ""
            }`}
            id="cvr-nr"
            value={cvrNr}
            onChange={(e) => setCvrNr(e.target.value)}
            placeholder="8 cifre"
            onBlur={() => setCvrNrTouched(true)}
          />
          <ValidationErrorMessage
            touched={cvrNrTouched}
            errorMessage={cvrNrError()}
          />
        </div>
        <div className="w-50 position-relative">
          <label className="form-label" htmlFor="p-nr">
            P-nummer <IsRequired />
          </label>
          <input
            type="text"
            className={`form-control${
              pNrTouched && pNrError() ? " is-invalid" : ""
            }`}
            id="p-nr"
            value={pNr}
            onChange={(e) => setPNr(e.target.value)}
            placeholder="10 cifre"
            onBlur={() => setPNrTouched(true)}
          />{" "}
          <ValidationErrorMessage
            touched={pNrTouched}
            errorMessage={pNrError()}
          />
        </div>
      </div>
      <div className="w-100">
        <CvrResult
          status={status}
          produktionsEnhed={produktionsEnhed}
          pnrError={pNrError()}
          cvrError={cvrNrError()}
          error={error}
        />
      </div>
    </>
  );
}

function CvrResult(props: {
  status: "error" | "success" | "pending";
  produktionsEnhed: IProduktionsenhed | undefined;
  pnrError: string;
  cvrError: string;
  error: AxiosError<CVROpslagRequestError> | null;
}) {
  if (props.cvrError) {
    return (
      <div className="small fw-semibold text-primary">{props.cvrError}</div>
    );
  }
  if (props.pnrError) {
    return (
      <div className="small fw-semibold text-primary">{props.pnrError}</div>
    );
  }
  if (props.status === "error") {
    return (
      <div className="alert alert-warning small p-2 px-3 mt-1 w-100">
        <h5 className="mb-1">Ingen resultater</h5>
        {props.error && (
          // <div className="text-dark">{props.error.response?.data.Message}</div>
          <div className="text-dark">
            Fandt ikke firma med angivet CVR- og P-nummer
          </div>
        )}
      </div>
    );
  }
  if (props.status === "pending") {
    return (
      <div className="text-secondary">
        <Spinner inline size={15} label="Henter ..." />
      </div>
    );
  }
  if (props.produktionsEnhed === undefined) {
    return (
      <div className="alert alert-warning small p-2 px-3 mt-1 w-100">
        Ingen data
      </div>
    );
  }
  return (
    <div className="c-ticket bg-white shadow border">
      <div className="c-ticket-content">
        <h5 className="c-ticket-heading">{props.produktionsEnhed.navn}</h5>
        <div className="c-ticket-sub flex-wrap">
          <span className="c-ticket-sub-item">
            CVR: {props.produktionsEnhed.cvrnummer}
          </span>
          <span className="c-ticket-sub-item"> | </span>
          <span className="c-ticket-sub-item">
            P-nr: {props.produktionsEnhed.pnummer}
          </span>
          <span className="c-ticket-sub-item"> | </span>
          <span className="c-ticket-sub-item">
            {props.produktionsEnhed.adresse}
          </span>
        </div>
      </div>
    </div>
  );
}
